import { ArrowForwardIosRounded } from "@mui/icons-material";
import { Button, ButtonBase, Card, Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import CircularLoading from "components/CircularLoading";
import { COMPANY_USER_ROLE_TITLE } from "constants/company";
import ROUTES from "constants/routes";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { UserContext } from "context/userContext";
import { authRedirect, setAuthCookies } from "helpers/cookieHelper";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const COMPANY_PREFIXES = new Set(["ООО", "АО", "ОАО", "ПАО", "ЗАО", "ИП", "LLC", "INC", "LTD"]);

const getCompanyInitials = (shortName: string) => {
  const normalizedWords = shortName
    .replace(/[«»"'`]/g, " ")
    .split(/[\s()\-–—,./]+/)
    .map((word) => word.trim())
    .filter(Boolean);

  const meaningfulWords = normalizedWords.filter((word) => !COMPANY_PREFIXES.has(word.toUpperCase()));
  const sourceWords = meaningfulWords.length ? meaningfulWords : normalizedWords;
  const initials = sourceWords
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() || "")
    .join("");

  return initials || shortName.slice(0, 2).toUpperCase();
};

const SelectCompany: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const { me, meError, meLoading, userCompanies, userCompaniesError, userCompaniesLoading } = useContext(UserContext);

  const [searchParams] = useSearchParams();
  const { redirectUrl } = useMemo(() => Object.fromEntries(searchParams), [searchParams]);
  const [initialLoading, setInitialLoading] = useState(true);

  const companies = userCompanies;
  const companyRolesById = useMemo(
    () =>
      new Map(
        (me?.userEmail.companyUsers || []).map((companyUser) => [
          companyUser.companyId,
          COMPANY_USER_ROLE_TITLE[companyUser.role],
        ])
      ),
    [me?.userEmail.companyUsers]
  );

  const handleNavigateAddNewCompany = () => navigate(`/${ROUTES.COMPANY_REGISTER}`);

  const onSelectCompany = (id: string) => {
    setAuthCookies({ name: "companyId", value: id });
    authRedirect({ redirectUrl });
  };

  useEffect(() => {
    if (!meLoading && (!me || meError)) {
      navigate(`/${ROUTES.LOGIN}${window.location.search}`, { replace: true });
    }
  }, [me, meError, meLoading, navigate]);

  useEffect(() => {
    if (!meLoading && me && !userCompaniesLoading) {
      if (companies?.length === 1) {
        const company = companies[0];
        onSelectCompany(company.id);
      } else if (Array.isArray(companies) && !companies.length) {
        navigate(`/${ROUTES.NO_COMPANY}`, { replace: true });
      } else {
        setInitialLoading(false);
      }
    }
  }, [companies, me, meLoading, navigate, userCompaniesLoading]);

  useEffect(() => {
    if (userCompaniesError) {
      addToast({ type: ToastTypeEnum.ERROR, text: "Не удалось получить список компаний" });
      setInitialLoading(false);
    }
  }, [addToast, userCompaniesError]);

  if (meLoading || userCompaniesLoading || initialLoading) return <CircularLoading />;

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: 664,
        mx: "auto",
        px: { xs: 2.5, sm: 4.5, md: 5 },
        py: { xs: 3, sm: 4, md: 4.5 },
        borderRadius: 4,
        border: "1px solid",
        borderColor: "#E1E8FF",
        boxShadow: "0 28px 72px rgba(37, 74, 196, 0.16)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,255,0.98) 100%)",
        backdropFilter: "blur(8px)",
      }}>
      <Stack spacing={3}>
        <Stack spacing={1.25}>
          <Typography
            variant='h28'
            sx={{
              color: "#0D2758",
              lineHeight: 1.12,
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}>
            Выберите пространство
          </Typography>

          <Typography
            variant='body16rg'
            sx={{
              color: "#35507E",
              fontSize: { xs: 17, sm: 18 },
              lineHeight: 1.55,
              maxWidth: 520,
            }}>
            Вы состоите в нескольких компаниях — откройте нужную.
          </Typography>
        </Stack>

        <Stack spacing={1.5}>
          {companies?.map((company) => {
            const companyRole = companyRolesById.get(company.id);

            return (
              <ButtonBase
                key={company.id}
                onClick={() => onSelectCompany(company.id)}
                sx={{
                  width: "100%",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "#D7E2FF",
                  backgroundColor: "common.white",
                  textAlign: "left",
                  px: { xs: 2, sm: 2.75 },
                  py: { xs: 2, sm: 2.25 },
                  justifyContent: "flex-start",
                  transition:
                    "transform .18s ease, box-shadow .18s ease, border-color .18s ease, background-color .18s ease",
                  "&:hover": {
                    borderColor: "#B7C9FF",
                    backgroundColor: "#F8FAFF",
                    boxShadow: "0 10px 28px rgba(41, 82, 227, 0.10)",
                    transform: "translateY(-1px)",
                  },
                  "&:focus-visible": {
                    outline: "none",
                    borderColor: "primary.main",
                    boxShadow: "0 0 0 4px rgba(23, 73, 209, 0.14)",
                  },
                }}>
                <Stack
                  direction='row'
                  spacing={{ xs: 1.5, sm: 2 }}
                  alignItems='center'
                  sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      flexShrink: 0,
                      borderRadius: { xs: 2.5, sm: 3 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "primary.main",
                      border: "1px solid #C7D4FF",
                      background: "linear-gradient(180deg, #EEF3FF 0%, #E6EDFF 100%)",
                      fontSize: { xs: 16, sm: 20 },
                      lineHeight: 1,
                      fontWeight: 700,
                      letterSpacing: "-0.03em",
                    }}>
                    {getCompanyInitials(company.shortName)}
                  </Box>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 1.25, sm: 2 }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent='space-between'
                    sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        color: "#071C46",
                        fontSize: { xs: 18, sm: 20 },
                        lineHeight: 1.4,
                        fontWeight: 500,
                        letterSpacing: "-0.025em",
                        wordBreak: "break-word",
                      }}>
                      {company.shortName}
                    </Typography>

                    {companyRole && (
                      <Box
                        component='span'
                        sx={{
                          flexShrink: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          minHeight: 40,
                          px: 1.75,
                          borderRadius: 2,
                          border: "1px solid #C7D4FF",
                          backgroundColor: "#EEF2FF",
                          color: "primary.main",
                          fontSize: { xs: 14, sm: 15 },
                          lineHeight: 1.2,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}>
                        {companyRole}
                      </Box>
                    )}
                  </Stack>

                  <ArrowForwardIosRounded
                    sx={{
                      ml: { xs: 0.5, sm: 1 },
                      flexShrink: 0,
                      color: "#9AA8C6",
                      fontSize: 20,
                    }}
                  />
                </Stack>
              </ButtonBase>
            );
          })}
        </Stack>

        <Button
          variant='text'
          onClick={handleNavigateAddNewCompany}
          sx={{
            alignSelf: "center",
            mt: 0.5,
            px: 0,
            color: "primary.main",
            fontSize: { xs: 18, sm: 19 },
            fontWeight: 500,
            lineHeight: 1.35,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
          }}>
          Зарегистрировать новую компанию
        </Button>
      </Stack>
    </Card>
  );
};

export default SelectCompany;
